/*
 Custom Cart Callbacks for Rebuy Smart Cart
 
 Handles dynamic UI updates for cart line items when discount codes are applied or removed.
 
 Key Features:
 - Tracks original cart item state (prices, sale status)
 - Calculates discounted prices using proportional allocation (RebuyUtils)
 - Updates DOM to display discounted prices with proper formatting
 - Reverts items to original state when discounts removed
 - Responds to cart events: change, ready, show, discount applied/removed
 
 Discount Support:
 - Manual codes (cart-level preview) - full price management
 - URL codes (checkout-level) - defers to Shopify, price management for > 1 items only
 - AUTO discounts (checkout-level) - defers to Shopify, price management for > 1 items only
 - Gift codes, manual or URL (0% and % off) - works with AUTO multiples
 - Multiples promos (3for2/4for3) - minimal processing, handled by MultiplesPromoHandler
 
 Technical Notes:
 - Uses signature tracking to avoid redundant DOM updates
 - Shopify-managed mode: skips processing when AUTO/URL multiples active
 - Combination scenarios: AUTO multiples + manual percentage discount
 - Relies on window.RebuyUtils for cart data and discount allocation
 
 Last Updated: October 2025 (Singles Day AUTO/URL support)
 */

window.RebuyCartCallbacks = (function () {
  // Private variables
  const originalStates = new Map();
  const lastProcessedState = {
    discountSignature: null,
    itemsSignature: null,
    lastUpdate: 0,
  };
  let debug = false;
  let discountAmountRetryTimer = null;
  let discountAmountRetryCount = 0;
  let automaticMultiplesRedrawRetryTimer = null;
  let automaticMultiplesRedrawRetryCount = 0;
  const automaticMultiplesRedrawRetryDelays = [100, 250, 500, 1000, 1500, 2000, 3000];
  const cartItemDOMRetryDelays = [50, 100, 200, 350];
  let urlDiscountRetryTimer = null;
  let urlDiscountRetryCount = 0;
  const urlDiscountRetryDelays = [100, 250, 500, 1000, 1500, 2000, 3000];

  function log(...args) {
    if (debug || window.bwbCartDebug === true) {
      console.log('[RebuyCartCallbacks]', ...args);
    }
  }

  function logStackedDiscountDiagnostics(data) {
    if (!(debug || window.bwbCartDebug === true)) return;

    console.groupCollapsed('[RebuyCartCallbacks] Stacked discount rendering');
    console.log(data);
    console.groupEnd();
  }

  // Check if a code is a valid multi-buy promo code
  function isValidMultiBuyCode(code) {
    if (!code) return false;
    const activeCodes = window.activeMultiBuyCodes.getActiveCodes();

    if (!Array.isArray(activeCodes)) return false;

    return activeCodes.includes(code.trim().toUpperCase());
  }

  function isShopifyAutomaticMultiplesLine(itemElement) {
    if (!itemElement) return false;

    const automaticCodes = [
      ...(window.activeMultiBuyCodes?.autoThreeForTwoCodes || []),
      ...(window.activeMultiBuyCodes?.autoFourForThreeCodes || []),
    ]
      .map(code => String(code).trim().toUpperCase())
      .filter(Boolean);

    if (!automaticCodes.length) return false;

    return Array.from(
      itemElement.querySelectorAll('.rebuy-cart__flyout-item-discount-message')
    ).some(message => {
      const messageText = message.textContent.toUpperCase();
      return automaticCodes.some(code => messageText.includes(code));
    });
  }

  function getPendingURLDiscountCode() {
    const code = window.RebuyUtils?.getDiscountCodeFromCookie?.();
    if (!code || isValidMultiBuyCode(code)) return null;

    return code;
  }

  function getCartItemIdentity(cartItem) {
    return window.RebuyUtils.getCartItemIdentity(cartItem);
  }

  function getCartItemElement(cartItem) {
    if (cartItem?.key) {
      const keyedElement = document.querySelector(`[data-item-key="${cartItem.key}"]`);
      if (keyedElement) return keyedElement;
    }

    const legacyMatches = document.querySelectorAll(`[data-item-id="${cartItem.id}"]`);
    return legacyMatches.length === 1 ? legacyMatches[0] : null;
  }

  function hasRenderedCartItemDOM(cart) {
    if (!Array.isArray(cart?.items) || cart.items.length === 0) return false;

    return cart.items.every(cartItem => {
      const itemElement = getCartItemElement(cartItem);
      return itemElement && itemElement.querySelector('.rebuy-cart__flyout-item-price');
    });
  }

  function isURLDiscountSettled(cart) {
    const discountData = window.RebuyUtils.getActiveDiscount();
    const hasCheckoutLevelDiscount = discountData?.source === 'checkout_level';

    return hasCheckoutLevelDiscount && hasRenderedCartItemDOM(cart);
  }

  function resetURLDiscountRetry() {
    urlDiscountRetryCount = 0;
    if (urlDiscountRetryTimer) {
      clearTimeout(urlDiscountRetryTimer);
      urlDiscountRetryTimer = null;
    }
  }

  function requestURLDiscountResilience(cart, resetAttempts = false) {
    const urlDiscountCode = getPendingURLDiscountCode();
    if (!urlDiscountCode || isURLDiscountSettled(cart)) {
      resetURLDiscountRetry();
      return;
    }

    if (resetAttempts) {
      resetURLDiscountRetry();
    }

    if (urlDiscountRetryTimer) return;

    const delay = urlDiscountRetryDelays[urlDiscountRetryCount];
    if (typeof delay === 'undefined') {
      log('URL discount UI did not settle before retry limit', { code: urlDiscountCode });
      return;
    }

    urlDiscountRetryCount += 1;
    log('Retrying delayed URL discount UI', {
      code: urlDiscountCode,
      attempt: urlDiscountRetryCount,
      delay,
    });

    urlDiscountRetryTimer = setTimeout(async () => {
      urlDiscountRetryTimer = null;
      const latestCart = await window.RebuyUtils.fetchCart();
      if (latestCart) {
        processCartItems(latestCart, true);
      }
      requestURLDiscountResilience(latestCart);
    }, delay);
  }

  function processURLDiscountLifecycle(cart) {
    resetAutomaticMultiplesRedrawRetry();
    processCartItems(cart);
    requestURLDiscountResilience(cart, true);
  }

  function refreshURLDiscountAfterPageRestore() {
    window.URLDiscountManager?.refresh?.();

    if (!getPendingURLDiscountCode()) return;

    window.RebuyUtils.fetchCart().then(cart => {
      if (!cart) return;
      processCartItems(cart, true);
      requestURLDiscountResilience(cart, true);
    });
  }

  function updateCustomSubtotal(subtotalTotal) {
    if (typeof subtotalTotal === 'number') {
      const subtotalSpan = document.querySelector(
        '.rebuy-cart__flyout-subtotal-final-amount span:last-child'
      );
      if (subtotalSpan) {
        subtotalSpan.textContent = subtotalTotal.toFixed(2).replace('.', ',') + '€';
      }
      return;
    }

    let total = 0;

    // Get ONLY the actual prices being paid (not compare-at strikethrough prices)
    document
      .querySelectorAll('.rebuy-cart__flyout-item-price')
      .forEach(priceContainer => {
        // Priority: sale price > regular price (never compare-at)
        let priceSpan = priceContainer.querySelector('.rebuy-money.sale span:last-child');
        
        if (!priceSpan) {
          priceSpan = priceContainer.querySelector('.rebuy-money:not(.compare-at):not(.sale) span:last-child');
        }

        if (!priceSpan) return;

        const price = parseFloat(priceSpan.textContent.replace('€', '').replace(',', '.'));

        // Filter out items that are made free by MultiplesPromoHandler
        const itemElement = priceContainer.closest('[data-item-id]');
        const IsMultiplesPromoFree = itemElement?.hasAttribute('data-multiples-promo-free');

        log('[updateCustomSubtotal] Found item:', {
          price,
          itemId: itemElement?.getAttribute('data-item-id'),
          IsMultiplesPromoFree,
        });

        // Only add to total if it's not a Multiples free item
        if (!IsMultiplesPromoFree) {
          total += price;
          log('Added to total:', price);
        } else {
          log('Skipped Multiples free item:', price);
        }
      });

    log('Total calculated:', total);

    const formattedTotal = total.toFixed(2).replace('.', ',') + '€';
    const subtotalSpan = document.querySelector(
      '.rebuy-cart__flyout-subtotal-final-amount span:last-child'
    );

    if (subtotalSpan) {
      log('Old subtotal:', subtotalSpan.textContent);
      subtotalSpan.textContent = formattedTotal;
      log('New subtotal:', formattedTotal);
    } else {
      log('Subtotal span not found');
    }
  }


  function shouldProcessCart(cart) {
    const currentDiscount = window.RebuyUtils.getActiveDiscount();
    const renderedDiscountAmount = window.RebuyUtils.getRenderedDiscountAmount();
    const itemsSignature = cart.items
      .map(item => `${getCartItemIdentity(item)}:${item.quantity}:${item.discounted_price || 'none'}`)
      .join('|');

    // Handle new discount object structure
    const hasNoDiscount = currentDiscount.type === 'none' || currentDiscount.hasDiscount === false;
    const hasValidDiscount =
      currentDiscount &&
      typeof currentDiscount === 'object' &&
      currentDiscount.isValid !== false &&
      !hasNoDiscount;
    const hasInvalidDiscount =
      currentDiscount && typeof currentDiscount === 'object' && currentDiscount.isValid === false;

    // Enhanced discount signature generation
    let discountSignature;
    if (hasNoDiscount) {
      discountSignature =
        originalStates.size > 0 ? 'no_discount:had_previous' : 'no_discount:clean';
    } else if (hasInvalidDiscount) {
      discountSignature = `${currentDiscount.value}:${currentDiscount.type}:${currentDiscount.source}:invalid:${renderedDiscountAmount}`;
    } else if (hasValidDiscount) {
      discountSignature = `${currentDiscount.value}:${currentDiscount.type}:${currentDiscount.source}:valid:${renderedDiscountAmount}`;
    } else {
      // Fallback for any unexpected structure
      discountSignature = String(currentDiscount);
    }

    const autoDiscountData = window.RebuyUtils.detectAutomaticBuyXGetY(cart);
    const hasAutoMultiples = autoDiscountData.isActive;

    const isManualRebuyDiscount = currentDiscount?.source === 'manual_rebuy';
    const isCheckoutLevelDiscount = currentDiscount?.source === 'checkout_level';
    const isCombinationScenario =
      hasAutoMultiples && (isManualRebuyDiscount || isCheckoutLevelDiscount);

    const hasChanges =
      lastProcessedState.discountSignature !== discountSignature ||
      lastProcessedState.itemsSignature !== itemsSignature ||
      Date.now() - lastProcessedState.lastUpdate > 5000 ||
      isCombinationScenario;

    if (hasChanges) {
      lastProcessedState.discountSignature = discountSignature;
      lastProcessedState.itemsSignature = itemsSignature;
      lastProcessedState.lastUpdate = Date.now();
      return true;
    }

    return false;
  }

  function calculatePrices(cartItem, discountPercent = 0, discountAllocation = null) {
    const originalState = originalStates.get(getCartItemIdentity(cartItem));
    const isSaleItem = originalState
      ? originalState.isSaleItem
      : cartItem.product?.selected_variant?.compare_at_price &&
        parseFloat(cartItem.product.selected_variant.compare_at_price) >
          parseFloat(cartItem.price) / 100
      ? true
      : false;

    const salePricePerUnit = originalState
      ? originalState.basePrice
      : parseFloat(cartItem.price) / 100;

    const fullPricePerUnit = isSaleItem
      ? cartItem.product?.selected_variant?.compare_at_price || null
      : null;

    let totalSalePrice = salePricePerUnit * cartItem.quantity;
    let totalCompareAtPrice = fullPricePerUnit ? fullPricePerUnit * cartItem.quantity : null;

    if (discountPercent > 0 && discountAllocation) {
      totalSalePrice = discountAllocation.discountedPrice;
    } else if (discountPercent > 0) {
      totalSalePrice = totalSalePrice * (1 - discountPercent / 100);
      totalSalePrice = Math.round(totalSalePrice * 100) / 100;
    }

    return {
      totalSalePrice: totalSalePrice.toFixed(2).replace('.', ','),
      totalCompareAtPrice: totalCompareAtPrice
        ? totalCompareAtPrice.toFixed(2).replace('.', ',')
        : null,
      hasDiscount: discountPercent > 0,
      isSaleItem: isSaleItem,
    };
  }

  function saveOriginalState(cartItem, itemElement, isSaleItem = null) {
    const cartItemIdentity = getCartItemIdentity(cartItem);
    if (!originalStates.has(cartItemIdentity)) {
      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (!priceContainer) {
        console.error(`[ERROR] No price container found for item ${cartItem.id}`);
        return;
      }

      const itemIsSale =
        isSaleItem !== null
          ? isSaleItem
          : priceContainer.querySelector('.rebuy-money.compare-at') !== null ||
            priceContainer.querySelector('.rebuy-money.sale') !== null;

      originalStates.set(cartItemIdentity, {
        html: priceContainer.innerHTML,
        basePrice: parseFloat(cartItem.price) / 100,
        isSaleItem: itemIsSale,
      });
    }
  }

  function scheduleCartItemDOMRetry(
    cartItem,
    totalSalePrice,
    totalCompareAtPrice,
    hasDiscount,
    isSaleItem,
    retryAttempt
  ) {
    if (retryAttempt >= cartItemDOMRetryDelays.length) return;

    setTimeout(() => {
      updateCartItemDOM(
        cartItem,
        totalSalePrice,
        totalCompareAtPrice,
        hasDiscount,
        isSaleItem,
        retryAttempt + 1
      );
    }, cartItemDOMRetryDelays[retryAttempt]);
  }

  function getCheckoutLevelDiscountCode() {
    const discountData = window.RebuyUtils.getActiveDiscount();
    const isCheckoutLevelDiscount =
      discountData &&
      typeof discountData === 'object' &&
      discountData.source === 'checkout_level' &&
      discountData.type !== 'none' &&
      discountData.type !== 'free_gift' &&
      discountData.hasDiscount !== false &&
      discountData.isValid !== false &&
      discountData.code;

    return isCheckoutLevelDiscount ? discountData.code.trim().toUpperCase() : null;
  }

  function syncCheckoutLevelDiscountMessage(itemElement, hasDiscount) {
    const fallbackAttribute = 'data-theme-checkout-discount-message';
    const existingMessages = Array.from(
      itemElement.querySelectorAll('.rebuy-cart__flyout-item-discount-message')
    );
    const nativeMessage = existingMessages.find(message => !message.hasAttribute(fallbackAttribute));
    const existingFallback = existingMessages.find(message => message.hasAttribute(fallbackAttribute));

    if (!hasDiscount) {
      if (existingFallback) existingFallback.remove();
      return;
    }

    // Rebuy's multiples handler reads native line messages to identify the
    // Shopify-owned automatic free split. Do not add a URL fallback message
    // while that flow is active: the footer tag/amount already communicate
    // the URL code, and the free line must remain entirely Shopify-managed.
    const hasAutomaticMultiples = window.RebuyUtils.detectAutomaticBuyXGetY(
      window.Rebuy?.Cart?.cart
    ).isActive;
    if (hasAutomaticMultiples) {
      if (existingFallback) existingFallback.remove();
      return;
    }

    const discountCode = getCheckoutLevelDiscountCode();

    if (!discountCode || nativeMessage) {
      if (existingFallback) existingFallback.remove();
      return;
    }

    const variantTitleElement = itemElement.querySelector('.rebuy-cart__flyout-item-variant-title');
    if (!variantTitleElement) return;

    const discountMessage = existingFallback || document.createElement('div');
    discountMessage.className = 'rebuy-cart__flyout-item-discount-message';
    discountMessage.setAttribute(fallbackAttribute, 'true');
    discountMessage.textContent = discountCode;

    if (!existingFallback) {
      variantTitleElement.insertAdjacentElement('afterend', discountMessage);
    }
  }

  function updateCartItemDOM(
    cartItem,
    totalSalePrice,
    totalCompareAtPrice,
    hasDiscount,
    isSaleItem,
    retryAttempt = 0
  ) {
    const itemElement = getCartItemElement(cartItem);
    if (!itemElement) {
      scheduleCartItemDOMRetry(
        cartItem,
        totalSalePrice,
        totalCompareAtPrice,
        hasDiscount,
        isSaleItem,
        retryAttempt
      );
      return;
    }

    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) {
      scheduleCartItemDOMRetry(
        cartItem,
        totalSalePrice,
        totalCompareAtPrice,
        hasDiscount,
        isSaleItem,
        retryAttempt
      );
      return;
    }

    saveOriginalState(cartItem, itemElement, isSaleItem);
    syncCheckoutLevelDiscountMessage(itemElement, hasDiscount);

    const numericSalePrice =
      typeof totalSalePrice === 'string'
        ? parseFloat(totalSalePrice.replace(',', '.'))
        : totalSalePrice;
    const numericComparePrice =
      totalCompareAtPrice && typeof totalCompareAtPrice === 'string'
        ? parseFloat(totalCompareAtPrice.replace(',', '.'))
        : totalCompareAtPrice;

    const formattedSalePrice = numericSalePrice.toFixed(2).replace('.', ',');
    const formattedComparePrice = numericComparePrice
      ? numericComparePrice.toFixed(2).replace('.', ',')
      : null;

    if (hasDiscount) {
      let compareAtPrice;
      if (isSaleItem) {
        const original = originalStates.get(getCartItemIdentity(cartItem));
        compareAtPrice = (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',');
      } else {
        compareAtPrice = ((parseFloat(cartItem.price) / 100) * cartItem.quantity)
          .toFixed(2)
          .replace('.', ',');
      }

      priceContainer.innerHTML = `
       <div>
         <span class="rebuy-money sale">
           <span class="sr-only">${window.themeVariables.strings.discountedPrice}</span>
           <span>${formattedSalePrice}€</span>
         </span>
         <span class="rebuy-money compare-at">
           <span class="sr-only">${window.themeVariables.strings.originalPrice}</span>
           <span>${compareAtPrice}€</span>
         </span>
       </div>
     `;
    } else if (isSaleItem) {
      priceContainer.innerHTML = `
       <div>
         <span class="rebuy-money sale">
           <span class="sr-only">${window.themeVariables.strings.salePrice}</span>
           <span>${formattedSalePrice}€</span>
         </span>
         <span class="rebuy-money compare-at">
           <span class="sr-only">${window.themeVariables.strings.comparePrice}</span>
           <span>${formattedComparePrice}€</span>
         </span>
       </div>
     `;
    } else {
      priceContainer.innerHTML = `
       <span class="rebuy-money">
         <span class="sr-only">${window.themeVariables.strings.price}</span>
         <span>${formattedSalePrice}€</span>
       </span>
     `;
    }
  }

  function revertItemToOriginal(cartItem, isSaleItem) {
    const cartItemIdentity = getCartItemIdentity(cartItem);
    const original = originalStates.get(cartItemIdentity);
    if (original) {
      const itemElement = getCartItemElement(cartItem);
      if (!itemElement) return;

      syncCheckoutLevelDiscountMessage(itemElement, false);

      const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
      if (priceContainer) {
        const currentQty = cartItem.quantity;
        const itemIsSale = isSaleItem !== undefined ? isSaleItem : original.isSaleItem;

        if (itemIsSale) {
          const salePrice = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          const compareAtPrice = cartItem.product?.selected_variant?.compare_at_price
            ? (cartItem.product.selected_variant.compare_at_price * currentQty)
                .toFixed(2)
                .replace('.', ',')
            : null;

          if (compareAtPrice) {
            priceContainer.innerHTML = `
             <div>
               <span class="rebuy-money sale">
                 <span class="sr-only">${window.themeVariables.strings.salePrice}</span>
                 <span>${salePrice}€</span>
               </span>
               <span class="rebuy-money compare-at">
                 <span class="sr-only">${window.themeVariables.strings.comparePrice}</span>
                 <span>${compareAtPrice}€</span>
               </span>
             </div>
           `;
          } else {
            priceContainer.innerHTML = `
             <span class="rebuy-money">
               <span class="sr-only">${window.themeVariables.strings.price}</span>
               <span>${salePrice}€</span>
             </span>
           `;
          }
        } else {
          const price = (original.basePrice * currentQty).toFixed(2).replace('.', ',');
          priceContainer.innerHTML = `
           <span class="rebuy-money">
             <span class="sr-only">${window.themeVariables.strings.price}</span>
             <span>${price}€</span>
           </span>
         `;
        }
      } else {
        console.error(`[ERROR] No price container found for cart item: ${cartItemIdentity}`);
      }
    }
  }

  function scheduleRenderedDiscountAmountRetry(cart) {
    if (discountAmountRetryTimer || discountAmountRetryCount >= 2) return;

    const delay = discountAmountRetryCount === 0 ? 150 : 350;
    discountAmountRetryCount += 1;
    discountAmountRetryTimer = setTimeout(() => {
      discountAmountRetryTimer = null;
      processCartItems(cart, true);
    }, delay);
  }

  function resetAutomaticMultiplesRedrawRetry() {
    automaticMultiplesRedrawRetryCount = 0;
    if (automaticMultiplesRedrawRetryTimer) {
      clearTimeout(automaticMultiplesRedrawRetryTimer);
      automaticMultiplesRedrawRetryTimer = null;
    }
  }

  function scheduleAutomaticMultiplesRedrawRetry(cart) {
    if (automaticMultiplesRedrawRetryTimer) return;

    const delay = automaticMultiplesRedrawRetryDelays[automaticMultiplesRedrawRetryCount];
    if (typeof delay === 'undefined') return;

    automaticMultiplesRedrawRetryCount += 1;
    automaticMultiplesRedrawRetryTimer = setTimeout(() => {
      automaticMultiplesRedrawRetryTimer = null;
      processCartItems(cart, true);
    }, delay);
  }

  async function processCartItems(cart, force = false) {
    if (!cart.items.length) return;

    if (!force && !shouldProcessCart(cart)) {
      log('[RebuyCartCallbacks] No changes detected, skipping DOM updates');
      return;
    }

    const discountData = window.RebuyUtils.getActiveDiscount();
    const hasValidDiscountBefore =
      lastProcessedState.discountSignature &&
      lastProcessedState.discountSignature !== 'no_discount:invalid' &&
      !lastProcessedState.discountSignature.includes('no_discount');

    // Handle both invalid discounts and removed discounts
    const noDiscountAtAll = discountData.type === 'none' || discountData.hasDiscount === false;
    const discountExistsButInvalid =
      discountData && typeof discountData === 'object' && discountData.isValid === false;
    const shouldRevert = (noDiscountAtAll && hasValidDiscountBefore) || discountExistsButInvalid;

    if (shouldRevert && originalStates.size > 0) {
      if (discountExistsButInvalid) {
        log('[ProcessCart] Discount invalid due to conditions (MOV, etc.) - reverting UI');
      } else {
        log('[ProcessCart] Discount removed - reverting UI');
      }

      // Revert all items to original state
      cart.items.forEach(cartItem => {
        if (originalStates.has(getCartItemIdentity(cartItem))) {
          revertItemToOriginal(cartItem);
        }
      });
      originalStates.clear(); // Clear tracking
      return; // Exit early
    }

    log('[RebuyCartCallbacks] Processing cart changes...');
    log(
      'Items signature:',
      cart.items
        .map(item => `${getCartItemIdentity(item)}:${item.quantity}:${item.discounted_price || 'none'}`)
        .join('|')
    );
    log('Discount signature:', lastProcessedState.discountSignature);

    try {
      const autoDiscountData = window.RebuyUtils.detectAutomaticBuyXGetY(cart);
      const hasAutoMultiples = autoDiscountData.isActive;
      const discountData = window.RebuyUtils.getActiveDiscount();
      const isManualRebuyDiscount =
        discountData &&
        typeof discountData === 'object' &&
        discountData.source === 'manual_rebuy';
      const manualCode =
        isManualRebuyDiscount && discountData.code
          ? discountData.code.trim().toUpperCase()
          : '';

      const isMultibuyPromoCode = manualCode && isValidMultiBuyCode(manualCode);
      if (isMultibuyPromoCode && !hasAutoMultiples) {
        log('[RebuyCartCallbacks] Multiples promo code detected');
        // A manually entered multiples code keeps the existing quantity-only path.
        // URL multiples are intentionally handled below with automatic multiples so
        // Shopify retains the free line while paid lines receive quantity totals.
        log('[RebuyCartCallbacks] Manual multiples code - processing quantity updates only');

        cart.items.forEach(cartItem => {
          const itemElement = getCartItemElement(cartItem);
          const isDiscountedByMultiples = itemElement?.hasAttribute('data-multiples-promo-free');

          if (!isDiscountedByMultiples) {
            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(getCartItemIdentity(cartItem))) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              const { totalSalePrice } = calculatePrices(cartItem);
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                      .toFixed(2)
                      .replace('.', ',')
                  : null,
                false,
                isSaleItem
              );
            }
          }
        });

        return;
      }

      // NEW: Enhanced detection for manual codes
      const isZeroPercentGiftCode =
        manualCode &&
        window.FreeGiftHandler &&
        window.FreeGiftHandler.isValidGiftCode(manualCode) &&
        discountData &&
        discountData.value === 0;

      const isManualDiscountCode = manualCode && !isZeroPercentGiftCode;
      const hasManualCode = isManualDiscountCode;

      log('[RebuyCartCallbacks] Detection:', {
        isManualRebuyDiscount,
        isManualDiscountCode,
        isZeroPercentGiftCode,
        hasManualCode,
        discountData,
      });

      if (hasAutoMultiples) {
        const renderedDiscountAmount = window.RebuyUtils.getRenderedDiscountAmount();
        // Keep the exact cart object selected by the automatic-allocation
        // detector. Variant ids and Rebuy keys can be shared by Shopify split
        // lines, whereas this reference is the actual €0.00 line.
        const freeItems = autoDiscountData.freeItems
          .map(freeItem => freeItem.lineItem)
          .filter(Boolean);
        const paidItems = cart.items.filter(item => !freeItems.includes(item));
        const paidSubtotal = paidItems.reduce(
          (total, item) => total + (parseFloat(item.price) / 100) * item.quantity,
          0
        );
        const hasRenderedManualDiscount =
          isManualRebuyDiscount && renderedDiscountAmount !== null && renderedDiscountAmount > 0;
        const hasURLCheckoutDiscount =
          discountData?.source === 'checkout_level' &&
          discountData.isValid !== false &&
          discountData.type !== 'free_gift' &&
          Math.abs(Number(discountData.value) || 0) > 0;
        let allocationResult = null;
        let allocationSource = 'automatic-multiples-only';

        if (hasRenderedManualDiscount) {
          allocationResult = window.RebuyUtils.allocateFixedDiscountsAcrossItems(
            paidItems,
            renderedDiscountAmount
          );
          allocationSource = 'automatic-multiples-with-rebuy-amount';
        } else if (hasURLCheckoutDiscount) {
          const urlDiscountAmount = Math.abs(Number(discountData.value));
          allocationResult =
            discountData.type === 'fixed_amount'
              ? window.RebuyUtils.allocateFixedDiscountsAcrossItems(paidItems, urlDiscountAmount)
              : window.RebuyUtils.allocateDiscountAcrossItems(paidItems, urlDiscountAmount);
          allocationSource = 'automatic-multiples-with-url-checkout-discount';
          // Shopify splits automatic lines after Rebuy has started its own
          // render. Reapply once that render settles so paid sale lines keep
          // their URL-reduced price without rewriting the Shopify free line.
          scheduleAutomaticMultiplesRedrawRetry(cart);
        }

        if (isManualRebuyDiscount && renderedDiscountAmount === null) {
          scheduleRenderedDiscountAmountRetry(cart);
        } else {
          discountAmountRetryCount = 0;
        }

        paidItems.forEach(cartItem => {
          const itemElement = getCartItemElement(cartItem);

          // The configured automatic code in Rebuy's native line message is
          // Shopify's authoritative free-line marker. Never let the URL
          // allocation or a delayed redraw retry rewrite that DOM line.
          if (isShopifyAutomaticMultiplesLine(itemElement)) {
            log('Skipping Shopify automatic free line:', cartItem.id);
            return;
          }

          const isSaleItem =
            cartItem.product?.selected_variant?.compare_at_price &&
            parseFloat(cartItem.product.selected_variant.compare_at_price) >
              parseFloat(cartItem.price) / 100;

          if (allocationResult) {
            const allocation = allocationResult.allocations.get(getCartItemIdentity(cartItem));
            if (!allocation) return;
            updateCartItemDOM(
              cartItem,
              allocation.discountedPrice,
              allocation.originalPrice,
              true,
              isSaleItem
            );
            return;
          }

          if (originalStates.has(getCartItemIdentity(cartItem))) {
            revertItemToOriginal(cartItem, isSaleItem);
          } else {
            const { totalSalePrice } = calculatePrices(cartItem);
            updateCartItemDOM(
              cartItem,
              totalSalePrice,
              isSaleItem
                ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                    .toFixed(2)
                    .replace('.', ',')
                : null,
              false,
              isSaleItem
            );
          }
        });

        updateCustomSubtotal(
          allocationResult ? allocationResult.discountedTotal : paidSubtotal
        );
        logStackedDiscountDiagnostics({
          rebuyState: window.Rebuy?.SmartCart?.discount?.discount || null,
          discountTags: Array.from(document.querySelectorAll('.rebuy-cart__discount-tag'))
            .filter(tag => tag.offsetParent !== null)
            .map(tag => tag.textContent.trim()),
          renderedDiscountAmount,
          freeItems: freeItems.map(item => ({ id: item.id, quantity: item.quantity })),
          paidItems: paidItems.map(item => ({ id: item.id, quantity: item.quantity })),
          branch: allocationSource,
          allocations: allocationResult
            ? Array.from(allocationResult.allocations.entries()).map(([id, allocation]) => ({ id, ...allocation }))
            : [],
          subtotal: allocationResult ? allocationResult.discountedTotal : paidSubtotal,
        });
        return; // Exit early for all auto Multiples scenarios
      }

      // MANUAL-ONLY DISCOUNT SCENARIOS
      log(
        '[RebuyCartCallbacks] No auto Multiples, processing manual discounts or quantity-only updates'
      );

      let currentDiscount, discountType;
      if (typeof discountData === 'object' && discountData !== null) {
        currentDiscount = discountData.value;
        discountType = discountData.type;
      } else {
        currentDiscount = discountData;
        discountType = 'percentage';
      }

      const discountPercent = Math.abs(currentDiscount);

      // Check if we have actual discount codes
      const hasDiscountCode = manualCode && manualCode.length > 0;
      const hasURLDiscount =
        discountData &&
        typeof discountData === 'object' &&
        discountData.source === 'checkout_level';
      const hasAnyDiscountCode = hasDiscountCode || hasURLDiscount;

      // Check for manual multi-promo code
      const isMultiBuyCode = window.activeMultiBuyCodes?.isValidCode
        ? window.activeMultiBuyCodes.isValidCode(manualCode)
        : false;

      if (isMultiBuyCode) {
        const config = window.activeMultiBuyCodes.getPromoConfig(manualCode);
        log(`[RebuyCartCallbacks] Manual ${config.label} code active, using minimal processing`);

        cart.items.forEach(cartItem => {
          try {
            if (parseInt(cartItem.price) === 0) return;

            const itemElement = getCartItemElement(cartItem);
            if (itemElement && itemElement.hasAttribute('data-multiples-promo-free')) {
              return; // Skip items managed by MultiplesPromoHandler
            }

            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            if (originalStates.has(getCartItemIdentity(cartItem))) {
              revertItemToOriginal(cartItem, isSaleItem);
            } else {
              const { totalSalePrice, totalCompareAtPrice } = calculatePrices(cartItem);
              updateCartItemDOM(
                cartItem,
                totalSalePrice,
                isSaleItem
                  ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                      .toFixed(2)
                      .replace('.', ',')
                  : null,
                false,
                isSaleItem
              );
            }
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });

        return; // Exit early for manual multi-buy
      }

      // Get exact subtotal for single-item carts
      let exactSubtotal = null;
      if (cart.items.length === 1) {
        const subtotalElement = document.querySelector('.rebuy-cart__flyout-subtotal-final-amount');
        if (subtotalElement) {
          const subtotalText = subtotalElement.textContent.trim();
          const subtotalMatch = subtotalText.match(/[\d.,]+/);
          if (subtotalMatch) {
            const numericText = subtotalMatch[0];
            const parsableText = numericText.replace(',', '.');
            exactSubtotal = parseFloat(parsableText);
          }
        }
      }

      // Skip 0% codes for discount allocation only (not quantity updates)
      if (discountPercent === 0 && hasAnyDiscountCode) {
        log(
          '[RebuyCartCallbacks] Zero-value code - skipping discount allocation, proceeding to quantity updates'
        );
        // Fall through to quantity-only logic below instead of returning
      } else if (
        currentDiscount < 0 &&
        discountData.type !== 'none' &&
        discountData.hasDiscount !== false &&
        discountData.isValid !== false
      ) {
        // REGULAR DISCOUNT CODES
        log('[RebuyCartCallbacks] Regular discount active, applying to all items');

        let allocations;
        if (discountType === 'fixed_amount') {
          const result = window.RebuyUtils.allocateFixedDiscountsAcrossItems(
            cart.items,
            discountPercent
          );
          allocations = result.allocations;
        } else {
          const result = window.RebuyUtils.allocateDiscountAcrossItems(cart.items, discountPercent);
          allocations = result.allocations;
        }

        cart.items.forEach(cartItem => {
          try {
            if (parseInt(cartItem.price) === 0) return;

            const isSaleItem =
              cartItem.product?.selected_variant?.compare_at_price &&
              parseFloat(cartItem.product.selected_variant.compare_at_price) >
                parseFloat(cartItem.price) / 100;

            const itemElement = getCartItemElement(cartItem);
            if (itemElement) {
              saveOriginalState(cartItem, itemElement, isSaleItem);
            }

            const allocation = allocations.get(getCartItemIdentity(cartItem));
            if (!allocation) {
              console.error(`[ERROR] No discount allocation found for item ${cartItem.id}`);
              return;
            }

            const original = originalStates.get(getCartItemIdentity(cartItem));

            // Calculate normally first
            let discountedPriceDisplay = allocation.discountedPrice.toFixed(2).replace('.', ',');

            // Simple rounding fix for single-item discounted carts
            if (cart.items.length === 1 && exactSubtotal !== null) {
              const lineItemPrice = allocation.discountedPrice;
              const cartSubtotal = exactSubtotal;
              const difference = Math.abs(lineItemPrice - cartSubtotal);

              log(
                '[ROUNDING FIX DEBUG] Line item:',
                lineItemPrice,
                'Cart subtotal:',
                cartSubtotal,
                'Difference:',
                difference
              );

              // If there's a small rounding difference, use the cart subtotal for consistency
              if (difference > 0 && difference <= 0.02) {
                log('[ROUNDING FIX DEBUG] Using cart subtotal for consistency');
                discountedPriceDisplay = cartSubtotal.toFixed(2).replace('.', ',');
              }
            }

            let compareAtPrice;
            if (isSaleItem) {
              compareAtPrice = original
                ? (original.basePrice * cartItem.quantity).toFixed(2).replace('.', ',')
                : null;
            } else {
              compareAtPrice = allocation.originalPrice.toFixed(2).replace('.', ',');
            }

            updateCartItemDOM(cartItem, discountedPriceDisplay, compareAtPrice, true, isSaleItem);
          } catch (error) {
            console.error(`Error processing cart item ${cartItem.id}:`, error);
          }
        });

        return; // Exit after discount processing
      }

      // NO DISCOUNT - QUANTITY UPDATES ONLY (now also handles 0% codes)
      log('[RebuyCartCallbacks] Processing quantity updates without discount');

      cart.items.forEach(cartItem => {
        try {
          log('[RebuyCartCallbacks] Updating item:', cartItem.id, 'quantity:', cartItem.quantity);
          if (parseInt(cartItem.price) === 0) return;

          const isSaleItem =
            cartItem.product?.selected_variant?.compare_at_price &&
            parseFloat(cartItem.product.selected_variant.compare_at_price) >
              parseFloat(cartItem.price) / 100;

          if (originalStates.has(getCartItemIdentity(cartItem))) {
            revertItemToOriginal(cartItem, isSaleItem);
          } else {
            const { totalSalePrice, totalCompareAtPrice, hasDiscount } = calculatePrices(cartItem);
            updateCartItemDOM(
              cartItem,
              totalSalePrice,
              isSaleItem
                ? (cartItem.product.selected_variant.compare_at_price * cartItem.quantity)
                    .toFixed(2)
                    .replace('.', ',')
                : null,
              hasDiscount,
              isSaleItem
            );
          }
        } catch (error) {
          console.error(`Error processing cart item ${cartItem.id}:`, error);
        }
      });
    } catch (error) {
      console.error('Error in processCartItems:', error);
    }
  }

  // Public API
  return {
    init: function () {
      if (!window.RebuyEventManager) {
        console.error('[RebuyCartCallbacks] ERROR: RebuyEventManager is not available!');
        return;
      }

      window.RebuyEventManager.on('cart:change', data => {
        processURLDiscountLifecycle(data.cart);
      })
        .on('cart:ready', data => {
          processURLDiscountLifecycle(data.cart);
        })
        .on('cart:show', data => {
          processURLDiscountLifecycle(data.cart);
        })
        .on('discount:applied', data => {
          processURLDiscountLifecycle(data.cart);
        })
        .on('discount:removed', data => {
          resetURLDiscountRetry();
          resetAutomaticMultiplesRedrawRetry();
          processCartItems(data.cart);
        });

      window.addEventListener('pageshow', refreshURLDiscountAfterPageRestore);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          refreshURLDiscountAfterPageRestore();
        }
      });
    },

    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },
  };
})();
